import { Component, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from '../../services/api.service';
import { LanguageService } from '../../services/language.service';
import { interval, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

@Component({
    selector: 'app-verify-email',
    templateUrl: './verify-email.component.html',
    styleUrls: ['./verify-email.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class VerifyEmailComponent implements OnInit, OnDestroy {
  isLoading = true;
  isSuccess = false;
  errorMessage = '';
  backendMessage = '';
  countdown = 5;
  private countdownSubscription?: Subscription;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private apiService: ApiService,
    private languageService: LanguageService
  ) {}

  ngOnInit(): void {
    const token = this.route.snapshot.queryParams['token'];
    
    if (!token) {
      this.handleError('TOKEN_MISSING');
      return;
    }

    this.verifyToken(token);
  }

  ngOnDestroy(): void {
    if (this.countdownSubscription) {
      this.countdownSubscription.unsubscribe();
    }
  }

  private verifyToken(token: string): void {
    this.apiService.subscribeVerify(token).subscribe(
      (response) => {
        this.isLoading = false;
        
        if (response?.error) {
          // Network/HTTP error occurred
          this.backendMessage = this.extractErrorMessage(response.error);
          this.handleError('BACKEND_ERROR');
        } else if (response?.Data) {
          // Check if verification was successful
          if (response.Data.Item1) {
            // Success
            this.isSuccess = true;
            this.backendMessage = response.Message || '';
          } else {
            // Failed verification
            this.backendMessage = response.Message || '';
            this.handleError('VERIFICATION_FAILED');
          }
          this.startCountdown();
        } else {
          // No data returned
          this.handleError('VERIFICATION_FAILED');
        }
      },
      (error) => {
        this.isLoading = false;
        this.backendMessage = this.extractErrorMessage(error);
        this.handleError('VERIFICATION_ERROR');
      }
    );
  }

  private extractErrorMessage(error: any): string {
    const currentLang = this.languageService.getCurrentLanguage();
    
    // Try to extract message from different error formats
    if (error?.error?.message) {
      return error.error.message;
    } else if (error?.message) {
      return error.message;
    } else if (error?.error) {
      return error.error;
    } else if (typeof error === 'string') {
      return error;
    }
    
    // Default error message if no specific message found
    return currentLang === 'tr' 
      ? 'Bilinmeyen bir hata oluştu.' 
      : 'An unknown error occurred.';
  }

  private handleError(errorType: string): void {
    this.isLoading = false;
    this.isSuccess = false;
    this.startCountdown();
  }

  getSuccessMessage(): string {
    const currentLang = this.languageService.getCurrentLanguage();
    return currentLang === 'tr' 
      ? 'E-posta adresiniz başarıyla doğrulandı!' 
      : 'Your email address has been successfully verified!';
  }

  getRedirectMessage(): string {
    const currentLang = this.languageService.getCurrentLanguage();
    return currentLang === 'tr' 
      ? `Anasayfaya dönmek için tıklayınız veya ${this.countdown} saniye bekleyiniz.` 
      : `Click to return to homepage or wait ${this.countdown} seconds.`;
  }

  hasBackendMessage(): boolean {
    return this.backendMessage.length > 0;
  }

  getErrorSubMessage(): string {
    const currentLang = this.languageService.getCurrentLanguage();
    return currentLang === 'tr' 
      ? 'Lütfen bültene tekrar kaydolarak yeni bir doğrulama e-postası talep ediniz.' 
      : 'Please re-subscribe to the newsletter to request a new verification email.';
  }

  private startCountdown(): void {
    this.countdownSubscription = interval(1000)
      .pipe(take(6)) // Take 6 emissions: 5, 4, 3, 2, 1, 0
      .subscribe((count) => {
        this.countdown = 5 - count; // Start from 5 and decrease
        if (this.countdown <= 0) {
          this.redirectToHome();
        }
      });
  }

  redirectToHome(): void {
    this.router.navigate(['/']);
  }
}
